import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { api, apiErrorText, CONTACTS_CHANGED } from '../lib/api.js';
import { useFetch } from '../lib/useFetch.js';
import { useLiveRefresh, useLiveVersion } from '../lib/useLiveRefresh.js';
import { maskZip, phoneValid, zipValid } from '../lib/contactFields.js';
import { writeContactParam } from '../lib/contactUrl.js';
import { chartOpenError } from '../lib/chartAccess.js';
import { clearApptParams, clearNoteParam } from '../lib/apptLink.js';
import { fetchNoteUsers } from '../lib/noteUsers.js';
import { FILE_HINT } from '../lib/uploads.js';
import { Async } from '../components/States.js';
import {
  ContactDrawer,
  ContactActionsMenu,
  type DrawerContact,
  type DrawerView,
} from './ContactDrawer.js';
import { useSession, canManage } from '../auth/session.js';
import { Dd, SearchableSelect, type DdOption } from '../components/Dropdown.js';
import { ContactSortModal } from '../components/ContactSortModal.js';
import {
  DEFAULT_SORT,
  SORT_LS_KEY,
  describeSort,
  normalizeSortPref,
  sortContactsBy,
  type SortPref,
  type SortValue,
} from '../lib/contactSort.js';
import { useContactAccessDialogs } from '../components/share/useContactAccessDialogs.js';
import { ShareFlow } from '../components/share/ShareFlow.js';
import { creatorCap, shareBody, type ShareDraft } from '../components/share/shareState.js';
import { fetchAccessPeople, postShare, type AccessPerson } from '../lib/contactAccess.js';
import { showError } from '../lib/dialogs.js';
import {
  CATEGORY_TO_POOL,
  FILE_ACCEPT,
  fileUploadProblem,
  ROLE_LABEL,
  type Role,
  type StatusPool,
  type ContactStatusRow,
} from '@ehr/shared';
import { buttonShown, useDashboardView, visibleCategories } from '../lib/dashboardView.js';
import {
  DuplicateContactDialog,
  duplicateOf,
  openableDup,
  isDupPhone,
  type DuplicateContact,
} from '../components/DuplicateContactNotice.js';
import { newClientProblems, NEW_CLIENT_REFERRAL_SEC } from '../lib/newClientProblems.js';
import { SaveBlockDialog, type SaveBlock } from '../components/SaveBlockDialog.js';
import {
  NEW_CLIENT_EMAIL_PROMPT,
  newClientEmailRefused,
  newClientLabel,
} from '../lib/newClientForm.js';
import {
  isOtherReferral,
  newClientReferralOtherMissing,
  REFERRAL_SPECIFY_PLACEHOLDER,
} from '../lib/referralOther.js';

interface Contact {
  id: string;
  category: string;
  firstName: string;
  lastName: string;
  email: string | null;
  /** ehr#178 - Demographics → MRN (copied onto the row); the column falls back to the short id. */
  mrn?: string | null;
  phone: string | null;
  stageId: string | null;
  statusId: string | null;
  /** V38 per-pool ladder status (context-menu STATUSES) — preferred for display. */
  contactStatusId: string | null;
  contactStatus?: { label: string; color: string } | null;
  ownerUserId: string | null;
  createdAt?: string;
  nextActionAt?: string | null;
  /** #59 - set when soft-deleted; drives the Archived view + Restore. */
  archivedAt?: string | null;
}
interface ContactList {
  items: Contact[];
  nextCursor: string | null;
}
interface Stage {
  id: string;
  name: string;
  position: number;
}
interface Status {
  id: string;
  name: string;
  position: number;
}
interface User {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  role?: string;
}

const catSvg = (p: string) =>
  `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0">${p}</svg>`;
const CATEGORIES = [
  {
    key: 'client',
    label: 'Clients',
    icon: catSvg(
      '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>',
    ),
  },
  {
    key: 'referral_source',
    label: 'Referral Sources',
    icon: catSvg(
      '<polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>',
    ),
  },
  {
    key: 'staff',
    label: 'Staff',
    icon: catSvg(
      '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
    ),
  },
] as const;

// ── New Client modal schema (1:1 with ehr-revised mockup) ───────────────────
// Status → dot colour map (the mockup stores colours per status; our API doesn't
// yet, so we mirror the prototype's palette by name and fall back to slate).
const STATUS_DOT: Record<string, string> = {
  New: '#0e7490',
  Active: '#1a8a66',
  'In Progress': '#2563eb',
  Prospective: '#7c3aed',
  Waitlist: '#b45309',
  'On Hold': '#b45309',
  Inactive: '#6b7280',
  Discharged: '#6b7280',
  Closed: '#6b7280',
};
const statusDot = (name: string) => STATUS_DOT[name] ?? '#8aaa9a';

const opt = (list: string[]): DdOption[] => list.map((v) => ({ value: v, label: v }));
const SALUTATION_OPTIONS = opt(['Mr.', 'Ms.', 'Mrs.', 'Mx.', 'Dr.', 'Prof.']);
const STATE_OPTIONS: DdOption[] = [
  ['AL', 'Alabama'],
  ['AK', 'Alaska'],
  ['AZ', 'Arizona'],
  ['AR', 'Arkansas'],
  ['CA', 'California'],
  ['CO', 'Colorado'],
  ['CT', 'Connecticut'],
  ['DE', 'Delaware'],
  ['DC', 'District of Columbia'],
  ['FL', 'Florida'],
  ['GA', 'Georgia'],
  ['HI', 'Hawaii'],
  ['ID', 'Idaho'],
  ['IL', 'Illinois'],
  ['IN', 'Indiana'],
  ['IA', 'Iowa'],
  ['KS', 'Kansas'],
  ['KY', 'Kentucky'],
  ['LA', 'Louisiana'],
  ['ME', 'Maine'],
  ['MD', 'Maryland'],
  ['MA', 'Massachusetts'],
  ['MI', 'Michigan'],
  ['MN', 'Minnesota'],
  ['MS', 'Mississippi'],
  ['MO', 'Missouri'],
  ['MT', 'Montana'],
  ['NE', 'Nebraska'],
  ['NV', 'Nevada'],
  ['NH', 'New Hampshire'],
  ['NJ', 'New Jersey'],
  ['NM', 'New Mexico'],
  ['NY', 'New York'],
  ['NC', 'North Carolina'],
  ['ND', 'North Dakota'],
  ['OH', 'Ohio'],
  ['OK', 'Oklahoma'],
  ['OR', 'Oregon'],
  ['PA', 'Pennsylvania'],
  ['RI', 'Rhode Island'],
  ['SC', 'South Carolina'],
  ['SD', 'South Dakota'],
  ['TN', 'Tennessee'],
  ['TX', 'Texas'],
  ['UT', 'Utah'],
  ['VT', 'Vermont'],
  ['VA', 'Virginia'],
  ['WA', 'Washington'],
  ['WV', 'West Virginia'],
  ['WI', 'Wisconsin'],
  ['WY', 'Wyoming'],
  ['PR', 'Puerto Rico'],
  ['AB', 'Alberta'],
  ['BC', 'British Columbia'],
  ['MB', 'Manitoba'],
  ['NB', 'New Brunswick'],
  ['NL', 'Newfoundland and Labrador'],
  ['NS', 'Nova Scotia'],
  ['ON', 'Ontario'],
  ['PE', 'Prince Edward Island'],
  ['QC', 'Quebec'],
  ['SK', 'Saskatchewan'],
  ['NT', 'Northwest Territories'],
  ['NU', 'Nunavut'],
  ['YT', 'Yukon'],
].map(([value, search]) => ({ value, label: value, search }) as DdOption);

// Demographic / clinical fields under "Full name" — order + types match the mockup.
type ClientField =
  | {
      key: string;
      label: string;
      type: 'text' | 'tel' | 'email' | 'date';
      full?: boolean;
      placeholder?: string;
      mask?: 'phone' | 'zip';
    }
  | { key: string; label: string; type: 'select'; options: DdOption[]; full?: boolean }
  | { key: string; label: string; type: 'subhead' };
// Add-Client fields (curated set). Name is split First/Last (both required); MRN
// is omitted — the "Client ID" shown in lists is derived from the contact UUID,
// not a stored value. Phones are live-masked. Everything here seeds the client's
// Demographics record on save.
// V4 (NEW_CLIENT_08-28-V4): Name / Phone & email / Address groups. Dropped from
// earlier versions: name pronunciation, role/title, DOB, contact status, MR#.
// MR# is derived from the contact id on save, not typed.
const PRONOUN_OPTIONS = opt([
  'She/her',
  'He/him',
  'They/them',
  'She/they',
  'He/they',
  'Ze/zir',
  'Prefer not to say',
  'Other',
]);
const CLIENT_FIELDS: ClientField[] = [
  { key: 'grp_name', label: 'Name', type: 'subhead' },
  { key: 'salutation', label: 'Salutation', type: 'select', options: SALUTATION_OPTIONS },
  { key: 'first_name', label: 'First name *', type: 'text' },
  { key: 'middle_name', label: 'Middle name', type: 'text' },
  { key: 'last_name', label: 'Last name *', type: 'text' },
  { key: 'preferred_name', label: 'Preferred name', type: 'text' },
  { key: 'pronouns', label: 'Pronouns', type: 'select', options: PRONOUN_OPTIONS },
  { key: 'grp_phone', label: 'Phone & email', type: 'subhead' },
  {
    key: 'phone',
    label: 'Mobile phone',
    type: 'tel',
    mask: 'phone',
    placeholder: '(555) 555-5555',
  },
  {
    key: 'other_phone',
    label: 'Other phone',
    type: 'tel',
    mask: 'phone',
    placeholder: '(555) 555-5555',
  },
  { key: 'email', label: 'Primary email', type: 'email', placeholder: 'name@example.com' },
  {
    key: 'secondary_email',
    label: 'Secondary email',
    type: 'email',
    placeholder: 'name@example.com',
  },
  { key: 'grp_address', label: 'Address', type: 'subhead' },
  { key: 'address', label: 'Street address', type: 'text', full: true },
  { key: 'address2', label: 'Apt / suite / unit', type: 'text', full: true },
  { key: 'city', label: 'City', type: 'text' },
  { key: 'state', label: 'State / Province', type: 'select', options: STATE_OPTIONS },
  { key: 'zip', label: 'ZIP code', type: 'text', mask: 'zip', placeholder: '12345' },
];

// Column rules (V27 "Fields") — Name is always shown; the rest are toggleable and
// persisted per-browser. Default set + labels mirror the mockup's Contacts table.
const OPTIONAL_COLUMNS = [
  { key: 'status', label: 'Status' },
  { key: 'email', label: 'Primary Email' },
  { key: 'clientId', label: 'MRN / Client ID' },
  { key: 'clinician', label: 'Assigned Clinician' },
  { key: 'intakeDate', label: 'Intake Date' },
  { key: 'nextAction', label: 'Next Action' },
  { key: 'phone', label: 'Phone' },
  { key: 'stage', label: 'Stage' },
] as const;
type ColumnKey = (typeof OPTIONAL_COLUMNS)[number]['key'];
const DEFAULT_COLUMNS: ColumnKey[] = [
  'status',
  'email',
  'clientId',
  'clinician',
  'intakeDate',
  'nextAction',
];
const COLS_LS_KEY = 'credify.contacts.columns.v2';
const VIEWS_LS_KEY = 'credify.contacts.views';
/** ehr#334: the Status filter's "No status" choice; the API reads it as contactStatusId IS NULL. */
export const NO_STATUS = 'none';
/** The Sort button names a column the Sort window doesn't offer by its header label. */
const columnLabel = (key: string) => OPTIONAL_COLUMNS.find((c) => c.key === key)?.label ?? 'Name';
/** The tick in a checked `.dd-check-box` (as MultiDd draws it). */
const checkMark = (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#fff"
    strokeWidth="3.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';

// Plural, human label for a contact category — the Show Columns panel scopes
// its changes per category ("Show Columns — Clients").
const categoryLabelPlural = (c: string) =>
  c === 'referral_source' ? 'Referral Sources' : c === 'staff' ? 'Staff' : 'Clients';

// Six-dot drag handle used in the Show Columns order list.
const ColDragIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <circle cx="9" cy="6" r="1.6" />
    <circle cx="15" cy="6" r="1.6" />
    <circle cx="9" cy="12" r="1.6" />
    <circle cx="15" cy="12" r="1.6" />
    <circle cx="9" cy="18" r="1.6" />
    <circle cx="15" cy="18" r="1.6" />
  </svg>
);
const fmtNextAction = (iso?: string | null) => {
  if (!iso) return '—';
  const days = Math.round((new Date(iso).getTime() - Date.now()) / 86_400_000);
  if (days === 0) return 'today';
  return days > 0 ? `in ${days}d` : `${-days}d ago`;
};
const shortId = (id: string) => id.slice(0, 8).toUpperCase();

interface SavedView {
  name: string;
  category: string;
  stageFilter: string;
  statusFilter: string;
  ownerFilter: string;
  search: string;
}

function loadJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

// Deterministic avatar colour from the id (mirrors the mockup's coloured avatars).
const AVATAR_COLORS = ['#1a8a66', '#0e7490', '#7c3aed', '#b45309', '#c0392b', '#3b72d9', '#0f766e'];
function avatarColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length]!;
}
const initials = (f: string, l: string) => `${f[0] ?? ''}${l[0] ?? ''}`;

function Avatar({ c }: { c: Contact }) {
  return (
    <span className="user-avatar" style={{ background: avatarColor(c.id) }} aria-hidden>
      {initials(c.firstName, c.lastName)}
    </span>
  );
}

export function ContactsTab({
  externalSearch,
  openContact,
  onOpenContactConsumed,
}: {
  externalSearch?: string;
  /** Deep-link request from another surface (e.g. Dashboard row click). */
  openContact?: { id: string; view?: DrawerView; record?: string } | null;
  onOpenContactConsumed?: () => void;
}) {
  const [category, setCategory] = useState<string>('client');
  // ehr#60 - Dashboard View: which category tabs show, their order and labels.
  const dashView = useDashboardView();
  const cats = visibleCategories(dashView).flatMap((k) => {
    const c = CATEGORIES.find((x) => x.key === k);
    return c ? [{ ...c, label: dashView.labels[k] || c.label }] : [];
  });
  useEffect(() => {
    if (cats.length && !cats.some((c) => c.key === category)) setCategory(cats[0]!.key);
  }, [cats, category]);
  const [search, setSearch] = useState('');

  // Header client-search drives the Contacts search (V27 top-bar parity).
  useEffect(() => {
    if (externalSearch !== undefined) setSearch(externalSearch);
  }, [externalSearch]);
  // Deep-link: another surface (Dashboard) asked us to open a specific contact's
  // drawer on a specific tab. Fetch it by id so it works even when the contact
  // isn't in the current filtered list, then clear the request. A ref-guard (not a
  // cancellation flag) dedupes StrictMode's double-run without cancelling the fetch
  // that consuming the request would otherwise abort; it resets once consumed so the
  // same row can be re-opened later.
  const deepLinkHandled = useRef<string | null>(null);
  // ehr#622: a link to a chart this user can't open (not owned, not shared) says so here, so they
  // don't take the list's only client for the one they asked for.
  const [openErr, setOpenErr] = useState('');
  useEffect(() => {
    const req = openContact;
    if (!req) {
      deepLinkHandled.current = null;
      return;
    }
    const key = `${req.id}:${req.view ?? ''}:${req.record ?? ''}`;
    if (deepLinkHandled.current === key) return;
    deepLinkHandled.current = key;
    api
      .get<DrawerContact>(`/contacts/${req.id}`)
      .then((c) => {
        setSelected(c);
        setDrawerView(req.view);
        setDrawerRecord(req.record);
      })
      .catch((e: unknown) => {
        // ehr#622: drop the link's one-shot params too (the session to write up, the note to
        // open), so the next chart opened doesn't pick them up.
        clearApptParams();
        clearNoteParam();
        writeContactParam(null);
        setOpenErr(chartOpenError(e));
      })
      .finally(() => onOpenContactConsumed?.());
  }, [openContact, onOpenContactConsumed]);
  const [view, setView] = useState<'list' | 'board'>('list');
  const [stageFilter, setStageFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [selected, setSelected] = useState<DrawerContact | null>(null);
  const [drawerView, setDrawerView] = useState<DrawerView | undefined>(undefined);
  // ehr#252: a record to open inside that tab (a shared form, from the bell or a hub link).
  const [drawerRecord, setDrawerRecord] = useState<string | undefined>(undefined);
  // ehr#622: opening a chart (a row, a link) clears the "can't open" notice.
  useEffect(() => {
    if (selected) setOpenErr('');
  }, [selected]);
  // Keep the URL's ?contact= in sync with the open drawer (covers every open path:
  // row click, deep-link command, and close). A transition guard avoids stripping
  // a fresh deep-link param on mount before the restore has populated `selected`.
  const contactUrlPrev = useRef<string | null>(null);
  useEffect(() => {
    const cur = selected?.id ?? null;
    if (cur === null && contactUrlPrev.current === null) return;
    writeContactParam(cur, drawerView);
    contactUrlPrev.current = cur;
  }, [selected, drawerView]);
  // Right-click contact context menu (Touches / Actions / Current Status / History).
  const [ctxMenu, setCtxMenu] = useState<{
    id: string;
    name: string;
    status?: string;
    x: number;
    y: number;
  } | null>(null);
  const [showNew, setShowNew] = useState(false);
  // #37: "+ Add {type}" opens the New-contact window straight away, on the base
  // type for the active tab (Clients → Client, Referral Sources → Referral Source,
  // Staff → Staff). No intermediary chooser.
  const [newType, setNewType] = useState<ContactTypeRow | null>(null);
  const contactTypes = useFetch<ContactTypeRow[]>(() => api.get('/contact-types'), []);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [myContacts, setMyContacts] = useState(false);
  // #59 - the Archived view lists soft-deleted contacts so they can be restored.
  const [showArchived, setShowArchived] = useState(false);
  const { user } = useSession();
  const manager = canManage(user?.role);

  // "My Contacts" scopes to the signed-in user as owner (V27 owner toggle).
  const effectiveOwner = myContacts ? (user?.id ?? '') : ownerFilter;

  // ehr#178: every page, not just the first 50 (an import past 50 clients never showed).
  const list = useFetch<ContactList>(async () => {
    const base =
      `/contacts?category=${category}&limit=100` +
      (search ? `&search=${encodeURIComponent(search)}` : '') +
      (stageFilter ? `&stage=${stageFilter}` : '') +
      // ehr#339: one or more statuses, comma-separated.
      (statusFilter ? `&contactStatus=${encodeURIComponent(statusFilter)}` : '') +
      (effectiveOwner ? `&owner=${effectiveOwner}` : '') +
      (showArchived ? '&archived=only' : '');
    const items: Contact[] = [];
    let cursor: string | null = null;
    for (let page = 0; page < 50; page++) {
      const r: ContactList = await api.get<ContactList>(
        base + (cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''),
      );
      items.push(...r.items);
      cursor = r.nextCursor;
      if (!cursor) break;
    }
    return { items, nextCursor: null };
  }, [category, search, stageFilter, statusFilter, effectiveOwner, showArchived]);
  // Stages power the Board columns (admin/manager; falls back to Unassigned-only).
  const stages = useFetch<Stage[]>(() => api.get('/settings/stages'), []);
  const statuses = useFetch<Status[]>(() => api.get('/settings/statuses'), []);
  // V38 STATUS = the per-pool ContactStatus ladder. The filter pill + bulk
  // "Set status" operate on the current tab's pool. (Switching tabs clears the
  // filter — see the category-tab onClick — so a stale cross-pool id can't linger.)
  const pool: StatusPool = CATEGORY_TO_POOL[category as keyof typeof CATEGORY_TO_POOL] ?? 'patient';
  const poolStatuses = useFetch<ContactStatusRow[]>(
    () => api.get(`/contact-statuses?pool=${pool}`),
    [pool],
  );
  const ladderStatuses = (poolStatuses.data ?? []).slice().sort((a, b) => a.position - b.position);
  const openCtxMenu = (
    e: { preventDefault: () => void; clientX: number; clientY: number },
    c: Contact,
  ) => {
    e.preventDefault();
    setCtxMenu({
      id: c.id,
      name: `${c.firstName} ${c.lastName}`.trim(),
      status: statuses.data?.find((s) => s.id === c.statusId)?.name,
      // Raw click point — ContactActionsMenu clamps to the viewport by measured size.
      x: e.clientX,
      y: e.clientY,
    });
  };
  // ehr#623: names from the provider cards for people who can't read /settings/users (a
  // clinician, or an admin viewing as one), so Assigned Clinician names the owner.
  const users = useFetch<User[]>(() => fetchNoteUsers(), []);
  // ehr#335 (reopened 10-13): the tab badges follow the search, My Contacts and Archived,
  // like the list (searching "chase" showed 5 rows under "Clients 42").
  const counts = useFetch<Record<string, number>>(() => {
    const q = new URLSearchParams({
      ...(search ? { search } : {}),
      ...(effectiveOwner ? { owner: effectiveOwner } : {}),
      ...(showArchived ? { archived: 'only' } : {}),
    }).toString();
    return api.get('/contacts/counts' + (q ? `?${q}` : ''));
  }, [search, effectiveOwner, showArchived]);
  // ehr#177/#178 - keep the list current without a page refresh: refetch quietly after a
  // contact change in this tab (owner, archive, Demographics, import...), when the list's
  // version stamp moves (another tab or user), and on focus / every 20 s as a backstop.
  const refreshList = () => {
    list.refresh();
    counts.refresh();
  };
  useEffect(() => {
    window.addEventListener(CONTACTS_CHANGED, refreshList);
    return () => window.removeEventListener(CONTACTS_CHANGED, refreshList);
  });
  useLiveVersion(() => api.get<{ version: string }>('/contacts/version'), refreshList);
  useLiveRefresh(refreshList);
  const userName = (u: User) => [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;
  // The contact's displayed status: the per-pool ladder rung (set from the context menu).
  // ehr#334: no rung is "No status", as the drawer says. It fell back to the legacy flat
  // pipeline status (usually "Active"), which no Status filter choice could find.
  const effectiveStatus = (c: Contact) => c.contactStatus?.label ?? null;

  const hasFilters = Boolean(stageFilter || statusFilter || ownerFilter);
  const clearAll = () => {
    setStageFilter('');
    setStatusFilter('');
    setOwnerFilter('');
  };

  // Column rules + saved views (Batch 6) — persisted per-browser.
  const [columns, setColumns] = useState<ColumnKey[]>(() =>
    loadJSON<ColumnKey[]>(COLS_LS_KEY, DEFAULT_COLUMNS),
  );
  // ehr#435 (Chase, 10-14): the Status filter and the Columns menu live behind one toolbar button,
  // "Status & Columns", with a tab for each. Column changes wait for Save; Close drops them.
  const [scTab, setScTab] = useState<'status' | 'columns' | null>(null);
  const [colDraft, setColDraft] = useState<ColumnKey[] | null>(null);
  const [showViews, setShowViews] = useState(false);
  const [advOpen, setAdvOpen] = useState(false);
  // Sortable list columns (mockup .list-head-cell). Client-side sort of the page.
  // ehr#339: the Sort window sets the same sort, and the choice is kept per browser.
  const [sortPref, setSortPref] = useState<SortPref>(() =>
    normalizeSortPref(loadJSON<unknown>(SORT_LS_KEY, null)),
  );
  const { key: sortKey, dir: sortDir } = sortPref;
  const saveSort = (next: SortPref) => {
    setSortPref(next);
    try {
      if (next.key) localStorage.setItem(SORT_LS_KEY, JSON.stringify(next));
      else localStorage.removeItem(SORT_LS_KEY);
    } catch {
      /* private mode: the sort still applies for this visit */
    }
  };
  const toggleSort = (key: string) =>
    saveSort(
      sortKey === key ? { key, dir: sortDir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    );
  const [sortOpen, setSortOpen] = useState(false);
  // ehr#339: the Status filter picks several (kept comma-joined, so saved views still load).
  const statusIds = statusFilter ? statusFilter.split(',') : [];
  const toggleStatus = (id: string) => {
    const next = statusIds.includes(id) ? statusIds.filter((x) => x !== id) : [...statusIds, id];
    // Keep the ladder's order, whatever order they were ticked in; "No status" goes last.
    setStatusFilter(
      [...ladderStatuses.map((s) => s.id), NO_STATUS].filter((s) => next.includes(s)).join(','),
    );
  };
  const scRef = useRef<HTMLDivElement>(null);
  const viewsRef = useRef<HTMLDivElement>(null);
  const closeSc = () => {
    setScTab(null);
    setColDraft(null);
  };

  // Close the Status & Columns / Views popovers on an outside click or Escape.
  useEffect(() => {
    if (!scTab && !showViews) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (scTab && scRef.current && !scRef.current.contains(t)) closeSc();
      if (showViews && viewsRef.current && !viewsRef.current.contains(t)) setShowViews(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeSc();
        setShowViews(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [scTab, showViews]);
  const [views, setViews] = useState<SavedView[]>(() => loadJSON<SavedView[]>(VIEWS_LS_KEY, []));
  const [viewName, setViewName] = useState('');
  const [showSave, setShowSave] = useState(false);

  const shows = (k: ColumnKey) => columns.includes(k);
  const persistColumns = (next: ColumnKey[]) => {
    setColumns(next);
    localStorage.setItem(COLS_LS_KEY, JSON.stringify(next));
  };
  // ehr#435: the Columns tab edits a draft (shown columns, in order); Save keeps it.
  const draft = colDraft ?? columns;
  const openSc = (tab: 'status' | 'columns') => {
    if (tab === 'columns' && !colDraft) setColDraft(columns.slice());
    setScTab(tab);
  };
  const toggleColumn = (k: ColumnKey) =>
    setColDraft((prev) => {
      const cur = prev ?? columns;
      return cur.includes(k) ? cur.filter((c) => c !== k) : [...cur, k];
    });
  // Reorder shown columns (drag or the up/down arrows) in the Columns tab.
  const moveColumn = (from: number, to: number) => {
    if (to < 0 || to >= draft.length || from === to) return;
    const next = draft.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved!);
    setColDraft(next);
  };
  const colDragIdx = useRef<number | null>(null);
  const resetColumns = () => setColDraft(DEFAULT_COLUMNS.slice());
  const saveColumns = () => {
    persistColumns(draft);
    closeSc();
  };
  const colLabel = (k: ColumnKey) => OPTIONAL_COLUMNS.find((c) => c.key === k)?.label ?? k;
  // Kebab → Columns opens the Columns tab (cross-component signal from App).
  useEffect(() => {
    const open = () => {
      setColDraft((d) => d ?? columns.slice());
      setScTab('columns');
    };
    window.addEventListener('credify:open-columns', open);
    return () => window.removeEventListener('credify:open-columns', open);
  }, [columns]);

  const persistViews = (next: SavedView[]) => {
    setViews(next);
    localStorage.setItem(VIEWS_LS_KEY, JSON.stringify(next));
  };
  const saveView = () => {
    const name = viewName.trim();
    if (!name) return;
    const v: SavedView = { name, category, stageFilter, statusFilter, ownerFilter, search };
    persistViews([...views.filter((x) => x.name !== name), v]);
    setViewName('');
    setShowSave(false);
  };
  const applyView = (name: string) => {
    const v = views.find((x) => x.name === name);
    if (!v) return;
    setCategory(v.category);
    setStageFilter(v.stageFilter);
    setStatusFilter(v.statusFilter);
    setOwnerFilter(v.ownerFilter);
    setSearch(v.search);
  };
  const deleteView = (name: string) => persistViews(views.filter((x) => x.name !== name));

  const stageName = (id: string | null) =>
    id ? ((stages.data ?? []).find((s) => s.id === id)?.name ?? '—') : '—';
  const ownerName = (id: string | null) => {
    if (!id) return '—';
    // ehr#623 (reopened 10-24): blank, not "—", until the names have loaded.
    if (!users.data) return '';
    const u = users.data.find((x) => x.id === id);
    return u ? userName(u) : '—';
  };

  const togglePick = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const pickAll = (ids: string[], on: boolean) => setPicked(on ? new Set(ids) : new Set());
  // ehr#730 (V11 bulk bar): Reassign and Share open the same windows as the chart, for the ticked
  // contacts; a save clears the ticks and reloads the list.
  const access = useContactAccessDialogs({
    onChanged: () => {
      setPicked(new Set());
      list.reload();
      counts.reload();
    },
  });
  const pickedTargets = () =>
    (list.data?.items ?? [])
      .filter((c) => picked.has(c.id))
      .map((c) => ({
        id: c.id,
        name: `${c.firstName} ${c.lastName}`.trim(),
        category: c.category,
        ownerUserId: c.ownerUserId,
      }));
  const runBulk = (
    action: 'stage' | 'status' | 'reassign' | 'archive' | 'unarchive',
    value?: string,
  ) => {
    const valueless = action === 'archive' || action === 'unarchive';
    if (picked.size === 0 || (!valueless && !value)) return;
    setBulkBusy(true);
    api
      .post('/contacts/bulk', { ids: [...picked], action, value })
      .then(() => {
        setPicked(new Set());
        list.reload();
        counts.reload();
      })
      .catch(() => undefined)
      .finally(() => setBulkBusy(false));
  };

  return (
    <>
      <div className="tabs" id="category-tabs">
        {cats.map((c) => (
          <button
            key={c.key}
            className={`tab ${category === c.key ? 'active' : ''}`}
            onClick={() => {
              setCategory(c.key);
              setStatusFilter(''); // status pool changes with the tab
            }}
          >
            <span className="chip-ico" aria-hidden dangerouslySetInnerHTML={{ __html: c.icon }} />
            {c.label}
            {/* ehr#335: the open tab counts its rows shown, so the Status / Advanced filters
                count too and the badge always matches the "total" on the right. */}
            {category === c.key && list.data ? (
              <span className="count">{list.data.items.length}</span>
            ) : (
              counts.data?.[c.key] != null && <span className="count">{counts.data[c.key]}</span>
            )}
          </button>
        ))}
      </div>
      <div className="subbar" id="subbar">
        <div className="subbar-filters" id="subbar-filters">
          {/* ehr#435 (Chase, 10-14): Status and Columns behind one button, a tab for each; the
              badge counts the statuses filtered. */}
          <div className="sc-wrap" ref={scRef}>
            <button
              type="button"
              className={`sc-btn ${scTab ? 'open' : ''} ${statusIds.length ? 'active' : ''}`}
              onClick={() => (scTab ? closeSc() : openSc('status'))}
              aria-expanded={Boolean(scTab)}
              aria-haspopup="dialog"
            >
              <svg viewBox="0 0 24 24" aria-hidden>
                <polygon
                  points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Status &amp; Columns
              {statusIds.length > 0 && <span className="sc-badge">{statusIds.length}</span>}
            </button>
            {scTab && (
              <div className="sc-panel" role="dialog" aria-label="Status and columns">
                <div className="sc-tabs" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={scTab === 'status'}
                    className={`sc-tab ${scTab === 'status' ? 'on' : ''}`}
                    onClick={() => openSc('status')}
                  >
                    Status
                    {statusIds.length > 0 && <span className="sc-badge">{statusIds.length}</span>}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={scTab === 'columns'}
                    className={`sc-tab ${scTab === 'columns' ? 'on' : ''}`}
                    onClick={() => openSc('columns')}
                  >
                    Columns
                  </button>
                </div>
                {scTab === 'status' ? (
                  <>
                    {/* ehr#339 (Chase, 10-13): a multi-select. Each status ticks on or off and
                        the list stays open; All statuses clears them. */}
                    <div className="sc-body" role="listbox" aria-multiselectable>
                      <button
                        type="button"
                        role="option"
                        aria-selected={statusIds.length === 0}
                        className={`fp-item fp-check sc-item ${statusIds.length ? '' : 'on'}`}
                        onClick={() => setStatusFilter('')}
                      >
                        <span
                          className={`dd-check-box${statusIds.length ? '' : ' on'}`}
                          aria-hidden
                        >
                          {!statusIds.length && checkMark}
                        </span>
                        All statuses
                      </button>
                      {/* ehr#334: "No status" finds the contacts on no rung (shown so in the list). */}
                      {[
                        ...ladderStatuses.map((st) => ({ id: st.id, label: st.label })),
                        { id: NO_STATUS, label: 'No status' },
                      ].map((st) => {
                        const on = statusIds.includes(st.id);
                        return (
                          <button
                            type="button"
                            role="option"
                            aria-selected={on}
                            key={st.id}
                            className={`fp-item fp-check sc-item ${on ? 'on' : ''}`}
                            onClick={() => toggleStatus(st.id)}
                          >
                            <span className={`dd-check-box${on ? ' on' : ''}`} aria-hidden>
                              {on && checkMark}
                            </span>
                            {st.label}
                          </button>
                        );
                      })}
                    </div>
                    <div className="sc-foot">
                      <button type="button" className="btn" onClick={closeSc}>
                        Close
                      </button>
                      <button type="button" className="btn" onClick={() => setStatusFilter('')}>
                        Clear
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="sc-body">
                      <div className="col-panel-links sc-links">
                        <button
                          type="button"
                          onClick={() =>
                            window.dispatchEvent(
                              new CustomEvent('credify:settings-shell', {
                                detail: {
                                  name: 'Column Rules',
                                  note: 'Column Rules — building in a later phase.',
                                },
                              }),
                            )
                          }
                        >
                          Column Rules
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            window.dispatchEvent(
                              new CustomEvent('credify:settings-shell', {
                                detail: {
                                  name: 'Set org default',
                                  note: 'Saving these columns as the org default needs an org-level setting — building in a later phase.',
                                },
                              }),
                            )
                          }
                        >
                          Set org default
                        </button>
                        <button type="button" onClick={resetColumns}>
                          Reset
                        </button>
                      </div>
                      <div className="sc-col-row required">
                        <span className="col-drag" aria-hidden>
                          <ColDragIcon />
                        </span>
                        <input type="checkbox" checked disabled aria-label="Name (required)" />
                        <span className="col-order-label">Name</span>
                        <span className="col-req-pill">Required</span>
                      </div>
                      {draft.map((k, i) => (
                        <div
                          key={k}
                          className="sc-col-row"
                          draggable
                          onDragStart={() => (colDragIdx.current = i)}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={() => {
                            const f = colDragIdx.current;
                            colDragIdx.current = null;
                            if (f != null) moveColumn(f, i);
                          }}
                        >
                          <span className="col-drag" aria-hidden>
                            <ColDragIcon />
                          </span>
                          <input
                            type="checkbox"
                            checked
                            onChange={() => toggleColumn(k)}
                            aria-label={`Show ${colLabel(k)}`}
                          />
                          <span className="col-order-label">{colLabel(k)}</span>
                          <span className="col-order-arrows">
                            <button
                              type="button"
                              aria-label={`Move ${colLabel(k)} up`}
                              disabled={i === 0}
                              onClick={() => moveColumn(i, i - 1)}
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              aria-label={`Move ${colLabel(k)} down`}
                              disabled={i === draft.length - 1}
                              onClick={() => moveColumn(i, i + 1)}
                            >
                              ↓
                            </button>
                          </span>
                        </div>
                      ))}
                      {OPTIONAL_COLUMNS.some((c) => !draft.includes(c.key)) && (
                        <>
                          <div className="col-panel-sub">Hidden</div>
                          {OPTIONAL_COLUMNS.filter((c) => !draft.includes(c.key)).map((c) => (
                            <label key={c.key} className="sc-col-row hidden">
                              <span className="col-drag" aria-hidden>
                                <ColDragIcon />
                              </span>
                              <input
                                type="checkbox"
                                checked={false}
                                onChange={() => toggleColumn(c.key)}
                              />
                              <span className="col-order-label">{c.label}</span>
                            </label>
                          ))}
                        </>
                      )}
                      <div className="col-panel-note">
                        Changes apply to {categoryLabelPlural(category)} only.
                      </div>
                    </div>
                    <div className="sc-foot">
                      <button type="button" className="btn" onClick={closeSc}>
                        Close
                      </button>
                      <button type="button" className="btn primary" onClick={saveColumns}>
                        Save
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          {/* ehr#339: Sort opens the Sort window (Chase's CRM sort-modal mockup). */}
          <button
            type="button"
            className={`adv-search-btn sort-btn ${sortKey ? 'active' : ''}`}
            onClick={() => setSortOpen(true)}
            aria-haspopup="dialog"
          >
            <span aria-hidden>&#8645;</span>
            Sort - {describeSort(sortPref, columnLabel)}
          </button>
          {/* Advanced search toggle (mockup: subbar-adv-btn) — reveals stage/owner/search/views */}
          <button
            className={`adv-search-btn ${advOpen ? 'active' : ''}`}
            onClick={() => setAdvOpen((o) => !o)}
            aria-expanded={advOpen}
          >
            <svg viewBox="0 0 24 24" aria-hidden>
              <circle cx="11" cy="11" r="8" fill="none" stroke="currentColor" strokeWidth="2" />
              <line
                x1="21"
                y1="21"
                x2="16.65"
                y2="16.65"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            Advanced
          </button>
          {/* My Contacts (mockup: access-rights) */}
          <button
            className={`filter-pill ${myContacts ? 'active' : ''}`}
            onClick={() => setMyContacts((m) => !m)}
            title="Only contacts assigned to me"
          >
            My Contacts
          </button>
          {/* #59 - Archived view: see and restore soft-deleted contacts. */}
          <button
            className={`filter-pill ${showArchived ? 'active' : ''}`}
            onClick={() => {
              setShowArchived((a) => !a);
              setPicked(new Set());
            }}
            title="Show archived contacts (restore them here)"
          >
            Archived
          </button>
          {/* History (mockup: global-history-btn) — stub */}
          <button className="btn" disabled title="Global history — coming soon">
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden>
              <path
                d="M3 3v5h5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M3.05 13A9 9 0 1 0 6 5.3L3 8"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 7v5l3 2"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            History
          </button>
          {buttonShown(dashView, 'referral_bucket') && (
            <div className="bucket-tip-wrap">
              <button className="btn bucket-pull-btn" disabled aria-label="Referrals bucket">
                Referrals<span className="bucket-count">0</span>
              </button>
              <span className="bk-tip">No unassigned referrals right now</span>
            </div>
          )}
          <button
            className="btn primary"
            onClick={() => {
              const t = baseTypeForCategory(category, contactTypes.data ?? []);
              if (t) {
                setNewType(t);
                setShowNew(true);
              }
            }}
          >
            +{' '}
            {category === 'referral_source'
              ? 'Add Referral Source'
              : category === 'staff'
                ? 'Add Staff'
                : 'Add Client'}
          </button>
        </div>
        <div className="subbar-stats" id="subbar-stats">
          <span>
            {/* #335 (reopened 10-12): the rows shown. The list loads every page, so its length
                follows the search, the filters, My Contacts and Archived. */}
            <strong>{list.data ? list.data.items.length : (counts.data?.[category] ?? 0)}</strong>{' '}
            total
          </span>
        </div>
      </div>

      {/* Advanced search panel — holds search + stage/owner + list/board + saved views. */}
      {advOpen && (
        <div className="adv-search-panel open" id="adv-search-panel">
          <div className="adv-row">
            <input
              className="subbar-search"
              placeholder="Search clients…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search contacts"
            />
            {/* #105 - Stage filter removed: it was mistaken for a status filter
                (Active/Inactive are stages), returning too few results. Filter by
                Status instead (the status pill in the main sub-bar). */}
            <Dd
              className={`pill-select ${ownerFilter ? 'active' : ''}`}
              ariaLabel="Filter by owner"
              searchable={false}
              placeholder="Any owner"
              value={ownerFilter}
              onChange={setOwnerFilter}
              options={[
                { value: '', label: 'Any owner' },
                ...(users.data ?? []).map((u) => ({ value: u.id, label: userName(u) })),
              ]}
            />
            <div className="view-toggle">
              <button className={view === 'list' ? 'active' : ''} onClick={() => setView('list')}>
                List
              </button>
              <button className={view === 'board' ? 'active' : ''} onClick={() => setView('board')}>
                Board
              </button>
            </div>
          </div>
          <div className="adv-search-actions">
            {views.length > 0 && (
              <div className="col-menu-wrap" ref={viewsRef}>
                <button
                  className="btn sm"
                  onClick={() => setShowViews((s) => !s)}
                  aria-expanded={showViews}
                >
                  Views ▾
                </button>
                {showViews && (
                  <div className="col-menu" role="menu" style={{ minWidth: 200 }}>
                    {views.map((v) => (
                      <div
                        key={v.name}
                        className="col-menu-item"
                        style={{ justifyContent: 'space-between' }}
                      >
                        <button
                          className="linklike"
                          onClick={() => {
                            applyView(v.name);
                            setShowViews(false);
                          }}
                        >
                          {v.name}
                        </button>
                        <button
                          className="btn sm"
                          aria-label={`Delete view ${v.name}`}
                          onClick={() => deleteView(v.name)}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {showSave ? (
              <>
                <input
                  placeholder="View name…"
                  value={viewName}
                  onChange={(e) => setViewName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && saveView()}
                  style={{ width: 140 }}
                  aria-label="Saved view name"
                />
                <button className="btn sm" onClick={saveView} disabled={!viewName.trim()}>
                  Save
                </button>
                <button className="btn sm" onClick={() => setShowSave(false)}>
                  Cancel
                </button>
              </>
            ) : (
              <button className="btn sm" onClick={() => setShowSave(true)}>
                Save view
              </button>
            )}
            {hasFilters && (
              <>
                <span className="spacer" style={{ flex: 1 }} />
                <button className="btn sm" onClick={clearAll}>
                  Clear all
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {manager && picked.size > 0 && (
        <div className="views-bar bulk-bar" role="region" aria-label="Bulk actions">
          <strong>{picked.size} selected</strong>
          <div style={{ width: 150 }}>
            <Dd
              value=""
              searchable={false}
              disabled={bulkBusy}
              ariaLabel="Set stage for selected"
              placeholder="Set stage…"
              onChange={(v) => v && runBulk('stage', v)}
              options={(stages.data ?? [])
                .slice()
                .sort((a, b) => a.position - b.position)
                .map((s) => ({ value: s.id, label: s.name }))}
            />
          </div>
          <div style={{ width: 150 }}>
            <Dd
              value=""
              searchable={false}
              disabled={bulkBusy}
              ariaLabel="Set status for selected"
              placeholder="Set status…"
              onChange={(v) => v && runBulk('status', v)}
              options={ladderStatuses.map((s) => ({ value: s.id, label: s.label }))}
            />
          </div>
          <button
            className="btn sm"
            onClick={() => access.openReassign(pickedTargets())}
            disabled={bulkBusy}
          >
            Reassign
          </button>
          {showArchived ? (
            <button className="btn sm" onClick={() => runBulk('unarchive')} disabled={bulkBusy}>
              Restore
            </button>
          ) : (
            <button className="btn sm" onClick={() => runBulk('archive')} disabled={bulkBusy}>
              Archive
            </button>
          )}
          <span className="spacer" style={{ flex: 1 }} />
          <button className="btn sm" onClick={() => setPicked(new Set())} disabled={bulkBusy}>
            Clear selection
          </button>
          <button
            className="btn sm primary"
            onClick={() => access.openShare(pickedTargets())}
            disabled={bulkBusy}
          >
            Share
          </button>
        </div>
      )}
      {access.element}

      <main id="main">
        {openErr && (
          <div className="contacts-open-err" role="alert">
            <span>{openErr}</span>
            <button
              type="button"
              className="btn sm"
              aria-label="Dismiss"
              onClick={() => setOpenErr('')}
            >
              Dismiss
            </button>
          </div>
        )}
        <div className="list-view">
          <Async
            state={list}
            emptyWhen={(d) => d.items.length === 0}
            emptyMessage="No contacts in this list."
          >
            {(d) => {
              if (view !== 'list') {
                return (
                  <Board
                    items={d.items}
                    stages={(stages.data ?? []).slice().sort((a, b) => a.position - b.position)}
                    onOpen={setSelected}
                    onContext={openCtxMenu}
                    onMove={(id, stageId) =>
                      api
                        .patch(`/contacts/${id}`, { stageId })
                        .then(() => list.reload())
                        .catch(() => undefined)
                    }
                  />
                );
              }
              const COL_WIDTH: Record<ColumnKey, string> = {
                status: 'minmax(90px,0.9fr)',
                email: 'minmax(150px,1.4fr)',
                clientId: '120px',
                clinician: 'minmax(110px,1fr)',
                intakeDate: '90px',
                nextAction: '90px',
                phone: '120px',
                stage: 'minmax(90px,0.9fr)',
              };
              const visibleCols = OPTIONAL_COLUMNS.filter((c) => shows(c.key));
              const gridCols = [
                manager ? '28px' : '',
                'minmax(150px,1.6fr)',
                ...visibleCols.map((c) => COL_WIDTH[c.key]),
                '24px',
              ]
                .filter(Boolean)
                .join(' ');
              const pill = (label: string) =>
                label && label !== '—' ? (
                  <span className="list-stage-pill">{label}</span>
                ) : (
                  <span className="muted">—</span>
                );
              const cellFor = (key: ColumnKey, c: Contact) => {
                switch (key) {
                  case 'status': {
                    const st = effectiveStatus(c);
                    return st ? (
                      pill(st)
                    ) : (
                      <span className="list-stage-pill no-status">No status</span>
                    );
                  }
                  case 'email':
                    return <span className="muted">{c.email ?? '—'}</span>;
                  case 'clientId':
                    return <span className="muted">{c.mrn || shortId(c.id)}</span>;
                  case 'clinician':
                    return <span className="muted">{ownerName(c.ownerUserId)}</span>;
                  case 'intakeDate':
                    return <span className="muted">{fmtDate(c.createdAt)}</span>;
                  case 'nextAction':
                    return <span className="muted">{fmtNextAction(c.nextActionAt)}</span>;
                  case 'phone':
                    return <span className="muted">{c.phone ?? '—'}</span>;
                  case 'stage':
                    return pill(stageName(c.stageId));
                }
              };
              // ehr#339: null (or a "—" name) is empty and sorts to the bottom either way.
              const named = (s: string) => (s === '—' ? null : s.toLowerCase());
              const sortVal = (key: string, c: Contact): SortValue => {
                switch (key) {
                  case 'name':
                    // #339 (reopened 10-12): by last name, then first, ignoring capitals.
                    return `${c.lastName} ${c.firstName}`.trim().toLowerCase();
                  case 'firstName':
                    return c.firstName.trim().toLowerCase();
                  case 'status':
                    return named(effectiveStatus(c) ?? '—');
                  case 'email':
                    return (c.email ?? '').toLowerCase();
                  case 'clientId':
                    return c.mrn || shortId(c.id);
                  case 'clinician':
                    return named(ownerName(c.ownerUserId));
                  case 'intakeDate':
                    return c.createdAt ? new Date(c.createdAt).getTime() : null;
                  case 'nextAction':
                    return c.nextActionAt ? new Date(c.nextActionAt).getTime() : null;
                  case 'phone':
                    return (c.phone ?? '').toLowerCase();
                  case 'stage':
                    return named(stageName(c.stageId));
                  default:
                    return '';
                }
              };
              const sortedItems = sortContactsBy(d.items, sortPref, sortVal);
              const headCell = (key: string, label: string) => (
                <span
                  key={key}
                  className={`list-cell list-head-cell ${sortKey === key ? 'sorted' : ''}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleSort(key)}
                >
                  <span className="lh-label">{label}</span>
                  <span
                    className="lh-caret"
                    style={
                      sortKey === key && sortDir === 'asc'
                        ? { transform: 'rotate(180deg)' }
                        : undefined
                    }
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                  <span className="col-resize" />
                </span>
              );
              return (
                <div className="list-table">
                  <div className="list-row head" style={{ gridTemplateColumns: gridCols }}>
                    {manager && (
                      <span className="list-cell bulk-cell">
                        <input
                          type="checkbox"
                          className="bulk-checkbox"
                          aria-label="Select all"
                          checked={d.items.length > 0 && picked.size === d.items.length}
                          onChange={(e) =>
                            pickAll(
                              d.items.map((c) => c.id),
                              e.target.checked,
                            )
                          }
                        />
                      </span>
                    )}
                    {headCell('name', 'Name')}
                    {visibleCols.map((col) => headCell(col.key, col.label))}
                    <span className="list-cell" />
                  </div>
                  {sortedItems.map((c) => (
                    <div
                      key={c.id}
                      className="list-row"
                      onClick={() => setSelected(c)}
                      onContextMenu={(e) => openCtxMenu(e, c)}
                      title="Open contact"
                      style={{ gridTemplateColumns: gridCols }}
                    >
                      {manager && (
                        <span className="list-cell" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="bulk-checkbox"
                            aria-label={`Select ${c.firstName} ${c.lastName}`}
                            checked={picked.has(c.id)}
                            onChange={() => togglePick(c.id)}
                          />
                        </span>
                      )}
                      <span className="list-cell list-name">
                        <span className="name-cell">
                          <Avatar c={c} />
                          <span className="list-name-link">
                            {c.firstName} {c.lastName}
                          </span>
                        </span>
                      </span>
                      {visibleCols.map((col) => (
                        <span key={col.key} className="list-cell">
                          {cellFor(col.key, c)}
                        </span>
                      ))}
                      <span className="list-cell row-arrow" aria-hidden>
                        →
                      </span>
                    </div>
                  ))}
                </div>
              );
            }}
          </Async>
        </div>
      </main>

      {selected && (
        <ContactDrawer
          // A requested record remounts the drawer so it opens on that tab even when this
          // contact's drawer is already open.
          key={drawerRecord ? `${selected.id}:${drawerRecord}` : selected.id}
          contact={selected}
          initialView={drawerView}
          initialRecord={drawerRecord}
          onClose={() => {
            setSelected(null);
            setDrawerView(undefined);
            setDrawerRecord(undefined);
          }}
          // ehr#208: a linked chart in the header opens that client's drawer.
          onOpenContact={(id) => {
            api
              .get<DrawerContact>(`/contacts/${id}`)
              .then((c) => {
                setSelected(c);
                setDrawerView(undefined);
                setDrawerRecord(undefined);
              })
              .catch(() => {});
          }}
        />
      )}
      {showNew && newType && (
        <NewContactModal
          contactType={newType}
          onClose={() => setShowNew(false)}
          onOpenExisting={(id) => {
            setShowNew(false);
            api
              .get<DrawerContact>(`/contacts/${id}`)
              .then((c) => {
                setSelected(c);
                setDrawerView(undefined);
                setDrawerRecord(undefined);
              })
              .catch(() => undefined);
          }}
          onCreated={(newId) => {
            setShowNew(false);
            list.reload();
            counts.reload();
            // Pop open the new client's Demographics drawer, pre-filled from
            // what was just entered.
            if (newId) {
              api
                .get<DrawerContact>(`/contacts/${newId}`)
                .then((c) => {
                  setSelected(c);
                  setDrawerView('demographics');
                })
                .catch(() => undefined);
            }
          }}
        />
      )}
      {ctxMenu && (
        <ContactActionsMenu
          key={ctxMenu.id}
          contactId={ctxMenu.id}
          contactName={ctxMenu.name}
          statusLabel={ctxMenu.status}
          positionStyle={{ position: 'fixed', left: ctxMenu.x, top: ctxMenu.y, zIndex: 1090 }}
          onClose={() => setCtxMenu(null)}
          onLogged={() => {
            // Reflect a status set / action fired back onto the list row + counts.
            list.reload();
            counts.reload();
          }}
        />
      )}
      {sortOpen && (
        <ContactSortModal
          value={sortPref}
          onApply={(next) => {
            saveSort(next);
            setSortOpen(false);
          }}
          onReset={() => {
            saveSort({ ...DEFAULT_SORT });
            setSortOpen(false);
          }}
          onClose={() => setSortOpen(false)}
        />
      )}
    </>
  );
}

// Admin "Testing & Distribution" routing panel — hidden for now (rules/backend
// land later). Flip to true to re-enable the QA distribution overrides.
const SHOW_ADMIN_ROUTING = false;

/** Progressive US phone mask: digits → (555) 555-5555, capped at 10 digits. */
const maskPhone = (raw: string): string => {
  const d = raw.replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

const isValidEmail = (v: string): boolean => !v || /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());

/**
 * Referral-source search-or-add combo (#37 / NEW_CLIENT V4), backed by the real
 * lookup: GET /lookups/referral-sources to list, POST to add. Typing filters; an
 * "Add" row creates a new source (admin/manager) and selects it. Emits the picked
 * source's id (contacts.referralSourceId) + its name for display.
 */
function ReferralCombo({
  value,
  name,
  onChange,
}: {
  value: string;
  name: string;
  onChange: (id: string, name: string) => void;
}) {
  const [items, setItems] = useState<{ id: string; name: string }[]>([]);
  const [q, setQ] = useState(name);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const load = () =>
    api
      .get<{ id: string; name: string }[]>('/lookups/referral-sources')
      .then((r) => setItems(Array.isArray(r) ? r : []))
      .catch(() => setItems([]));
  useEffect(() => {
    void load();
  }, []);
  useEffect(() => {
    setQ(name);
  }, [name]);
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);
  const typed = q.trim();
  const ql = typed.toLowerCase();
  const shown = ql ? items.filter((i) => i.name.toLowerCase().includes(ql)) : items;
  const exact = items.some((i) => i.name.toLowerCase() === ql);
  const pick = (i: { id: string; name: string }) => {
    onChange(i.id, i.name);
    setQ(i.name);
    setOpen(false);
  };
  const add = async () => {
    if (!typed || busy) return;
    setBusy(true);
    try {
      const created = await api.post<{ id: string; name: string }>('/lookups/referral-sources', {
        name: typed,
      });
      await load();
      onChange(created.id, created.name);
      setQ(created.name);
      setOpen(false);
    } catch {
      /* leave the field; the user can retry */
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`dd${open ? ' dd-open' : ''}`} ref={ref} style={{ position: 'relative' }}>
      <input
        type="text"
        placeholder="Select or add…"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
          if (!e.target.value.trim()) onChange('', '');
        }}
        onFocus={() => setOpen(true)}
      />
      {open && (
        <div className="dd-panel" role="listbox" style={{ maxHeight: 260, overflow: 'auto' }}>
          {shown.map((i) => (
            <button
              type="button"
              key={i.id}
              className={`dd-opt${i.id === value ? ' sel' : ''}`}
              onClick={() => pick(i)}
            >
              <span className="dd-opt-label">{i.name}</span>
            </button>
          ))}
          {shown.length === 0 && !typed && (
            <div className="searchable-select-empty">No referral sources yet</div>
          )}
          {typed && !exact && (
            <button
              type="button"
              className="dd-opt"
              style={{ color: 'var(--green-primary)', fontWeight: 600 }}
              onClick={() => void add()}
            >
              {busy ? 'Adding…' : `Add "${typed}"`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** New Contact modal — POST /contacts (admin/manager/front_desk). */
const pruneEmpty = (o: Record<string, string | undefined>): Record<string, string> =>
  Object.fromEntries(Object.entries(o).filter(([, v]) => v && String(v).trim())) as Record<
    string,
    string
  >;

/**
 * Add Contact modal — mockup `openNewModal` port (.modal-head + .field-grid).
 * Shared by the Contacts subbar and the Dashboard quick-action. On save it
 * creates the contact and, for Clients, seeds the encrypted Demographics record
 * from the extra fields (priority 4: add-client → demographics mapping).
 */
// #37: the New-contact window is driven by a Contact Type (the 7 system types from
// the Contact Types manager). Each type carries an optional category link; when it is
// null we infer the category from the name so status pools and payloads still line up.
export type ContactTypeRow = {
  id: string;
  name: string;
  active: boolean;
  system: boolean;
  position: number;
  category: string | null;
};
function inferTypeCategory(name: string): string {
  if (/referral/i.test(name)) return 'referral_source';
  if (/staff/i.test(name)) return 'staff';
  return 'client';
}

/** The base system type each category tab's "+ Add" button opens. */
const CATEGORY_BASE_TYPE: Record<string, string> = {
  client: 'Client',
  referral_source: 'Referral Source',
  staff: 'Staff',
};
/** Resolve the type a "+ Add {type}" button should open for the active tab. */
export function baseTypeForCategory(
  category: string,
  types: ContactTypeRow[],
): ContactTypeRow | null {
  const active = types.filter((t) => t.active);
  const want = CATEGORY_BASE_TYPE[category];
  return (
    active.find((t) => t.name === want) ??
    active.find((t) => (t.category ?? inferTypeCategory(t.name)) === category) ??
    null
  );
}

export function NewContactModal({
  contactType,
  onClose,
  onCreated,
  onOpenExisting,
}: {
  contactType: ContactTypeRow;
  onClose: () => void;
  onCreated: (newId?: string) => void;
  /** ehr#482 / #500: open the contact that already has this phone or email. */
  onOpenExisting?: (id: string) => void;
}) {
  const { user } = useSession();
  // #37: the contact type is chosen in the chooser popup that precedes this window;
  // it fixes the category (and status pool) and titles the window. Every type shares
  // this same full (V4) field set.
  const contactTypeId = contactType.id;
  const [f, setF] = useState<Record<string, string>>({});
  const set = (k: string) => (e: { target: { value: string } }) =>
    setF((p) => ({ ...p, [k]: e.target.value }));
  const setV = (k: string) => (v: string) => setF((p) => ({ ...p, [k]: v }));
  const [sharing, setSharing] = useState<'self' | 'shared'>('self');
  // ehr#730: the Share flow's picks (Who, Access per tab), saved once the contact exists.
  const [shareCfg, setShareCfg] = useState<{ draft: ShareDraft; summary: string } | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [sharePeople, setSharePeople] = useState<AccessPerson[] | null>(null);
  const openShareFlow = () => {
    if (sharePeople) {
      setShareModalOpen(true);
      return;
    }
    fetchAccessPeople()
      .then((p) => {
        setSharePeople(p);
        setShareModalOpen(true);
      })
      .catch((e: unknown) => {
        setSharing('self');
        showError(apiErrorText(e, 'Could not load the people to share with.'));
      });
  };
  const [files, setFiles] = useState<File[]>([]);
  const [fileMsg, setFileMsg] = useState('');
  const [adminOpen, setAdminOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dup, setDup] = useState<DuplicateContact | null>(null);
  // ehr#482: the red pop-up is open (Go back closes it; the taken field stays red until changed),
  // and the email the refused save sent (its field is red while it still holds that email).
  const [dupOpen, setDupOpen] = useState(false);
  const [dupEmail, setDupEmail] = useState('');
  const refuse = (d: DuplicateContact) => {
    setDup(d);
    setDupEmail((f.email ?? '').trim().toLowerCase());
    setDupOpen(true);
  };
  const dupField = (key: string): boolean =>
    !!dup &&
    (dup.field === 'email'
      ? key === 'email' && (f.email ?? '').trim().toLowerCase() === dupEmail
      : (key === 'phone' || key === 'other_phone') && isDupPhone(dup, f[key]));
  // Holds the created contact id across a retry so a lost-response POST can't
  // spawn a duplicate on the second Save click.
  const createdIdRef = useRef<string | null>(null);
  const statuses = useFetch<Status[]>(() => api.get('/settings/statuses'), []);
  const isAdmin = user?.role === 'admin';
  // Category (client / referral_source / staff) is derived from the chosen type —
  // its explicit link, or inferred from the name when null.
  const category = contactType.category ?? inferTypeCategory(contactType.name);

  // Status dropdown options (with mockup-parity colour dots); default to "New".
  const statusOpts: DdOption[] = (statuses.data ?? [])
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((s) => ({ value: s.id, label: s.name, dot: statusDot(s.name) }));
  useEffect(() => {
    if (!f.statusId && statusOpts.length) {
      const dflt = statusOpts.find((o) => o.label === 'New') ?? statusOpts[0];
      if (dflt) setF((p) => ({ ...p, statusId: dflt.value }));
    }
  }, [statuses.data]);

  const derivedFirst = (f.first_name ?? '').trim();
  const derivedLast = (f.last_name ?? '').trim();
  // ehr#758: Referral source "Other" needs its "Please specify" box (red while it is blank).
  const referralOtherMissing = newClientReferralOtherMissing(
    f.referralSourceName,
    f.referralSourceOther,
  );
  // ehr#760: a blocked Save says why in the client chart's "This record was not saved" pop-up,
  // and the blank required fields stay red (ehr#734 mobile phone, ehr#757 primary email for a
  // client) once tried.
  const [tried, setTried] = useState(false);
  const [saveBlock, setSaveBlock] = useState<SaveBlock | null>(null);
  const problems = newClientProblems(
    CLIENT_FIELDS,
    f,
    category,
    isValidEmail,
    referralOtherMissing,
  );
  const blankRequired = (key: string) => tried && problems.missing.some((m) => m.field === key);
  const modalRef = useRef<HTMLDivElement>(null);
  const goToField = (_sec: string, field: string) => {
    setSaveBlock(null);
    window.setTimeout(() => {
      const el = modalRef.current?.querySelector<HTMLElement>(`[data-field="${field}"]`);
      if (!el) return;
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      const target = el.matches('input, textarea, button')
        ? el
        : el.querySelector<HTMLElement>('input, textarea, button');
      target?.focus({ preventScroll: true });
    }, 60);
  };

  const save = async () => {
    // ehr#9: a phone is saved only as a full 10-digit number, an email only when it looks like one.
    if (problems.missing.length || problems.bad.length) {
      setTried(true);
      setError(null);
      setSaveBlock(problems);
      return;
    }
    setSaving(true);
    setError(null);
    setDup(null);
    try {
      // ehr#482 / #500: a phone or email another contact already uses is refused before anything
      // is created (the server refuses it too); the red pop-up offers that contact instead.
      if (!createdIdRef.current) {
        const qs = new URLSearchParams();
        const email = (f.email ?? '').trim();
        if (email) qs.set('email', email);
        for (const p of [f.phone, f.other_phone])
          if ((p ?? '').trim()) qs.append('phone', p!.trim());
        const hit = qs.toString()
          ? await api.get<DuplicateContact | null>(`/contacts/duplicate-check?${qs}`)
          : null;
        if (hit) {
          refuse(hit);
          return;
        }
      }
      // Duplicate-submit guard: if a prior attempt already created the contact
      // (e.g. the POST reached the server but its response was lost to a network
      // blip / a deploy restart), reuse that id instead of creating a second one.
      let id = createdIdRef.current;
      if (!id) {
        const created = await api.post<{ id: string }>('/contacts', {
          category,
          contactTypeId: contactTypeId || undefined,
          firstName: derivedFirst,
          lastName: derivedLast,
          email: (f.email ?? '').trim() || undefined,
          phone: (f.phone ?? '').trim() || undefined,
          referralSourceId: (f.referralSourceId ?? '').trim() || undefined,
        });
        id = created?.id ?? null;
        createdIdRef.current = id;
      }
      if (id) {
        if (f.statusId || f.ownerUserId) {
          await api
            .patch(`/contacts/${id}`, {
              statusId: f.statusId || undefined,
              ownerUserId: f.ownerUserId || undefined,
            })
            .catch(() => undefined);
        }
        {
          // V22/V4 layout: name/pronouns/phones/email/address live under `contact`.
          // Seeding these makes the Demographics drawer we pop open next come up
          // pre-filled. Referral is now the contact's FK (set in the create above);
          // DOB and the free-text referral field were dropped from the window (V4).
          const demo = {
            contact: pruneEmpty({
              firstName: derivedFirst,
              middleName: f.middle_name,
              lastName: derivedLast,
              salutation: f.salutation,
              preferredName: f.preferred_name,
              pronouns: f.pronouns,
              pronounsOther: f.pronouns === 'Other' ? f.pronounsOther : '',
              mobilePhone: f.phone,
              phone: f.other_phone,
              email: f.email,
              portalEmail: f.secondary_email,
              address1: f.address,
              address2: f.address2,
              city: f.city,
              state: f.state,
              zip: f.zip,
            }),
            // ehr#758: the Other referral source's write-in, shown on the chart's Referral section.
            ...(isOtherReferral(f.referralSourceName) && (f.referralSourceOther ?? '').trim()
              ? { referral: { referralSourceOther: f.referralSourceOther!.trim() } }
              : {}),
          };
          if (Object.keys(demo.contact).length || demo.referral) {
            await api.put(`/contacts/${id}/demographics`, demo).catch(() => undefined);
          }
        }
        // Ownership → "Add for myself & share" (ehr#730 V11): the Share flow's picks, on the new contact.
        if (sharing === 'shared' && shareCfg) {
          const target = { id, name: '', category, ownerUserId: user?.id ?? null };
          await postShare(shareBody(shareCfg.draft, [target], null)).catch((e: unknown) =>
            showError(
              `The contact was added, but it was not shared. ${apiErrorText(e, '')}`.trim(),
            ),
          );
        }
        // Upload the staged attachments now that the contact exists (#12). Each is
        // already ≤ 2 MB (addFiles rejects larger ones) and sent as base64 JSON.
        if (files.length) {
          await Promise.all(
            files.map(
              (file) =>
                new Promise<void>((resolve) => {
                  const reader = new FileReader();
                  reader.onload = () => {
                    const dataBase64 = String(reader.result).split(',')[1] ?? '';
                    api
                      .post(`/contacts/${id}/files`, {
                        name: file.name,
                        mime: file.type || 'application/octet-stream',
                        dataBase64,
                      })
                      .catch(() => undefined)
                      .finally(() => resolve());
                  };
                  reader.onerror = () => resolve();
                  reader.readAsDataURL(file);
                }),
            ),
          );
        }
      }
      // Hand the new id back so the caller can pop open its Demographics drawer.
      onCreated(id ?? undefined);
    } catch (e: unknown) {
      const d = duplicateOf(e);
      if (d) refuse(d);
      else if (newClientEmailRefused(e)) {
        setTried(true);
        setError(NEW_CLIENT_EMAIL_PROMPT);
      } else setError(e instanceof Error ? e.message : 'Could not create');
    } finally {
      setSaving(false);
    }
  };

  // Staged attachments — uploaded to the contact-files backend after the contact
  // is created (see save()). Oversized files are rejected up front (#12).
  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const picked = Array.from(list);
    // ehr#321: the allowed types and 4.5 MB, checked up front (the API checks them again).
    const problems = picked.map((f) => fileUploadProblem(f)).filter((p): p is string => !!p);
    const ok = picked.filter((f) => !fileUploadProblem(f));
    if (ok.length) setFiles((p) => [...p, ...ok]);
    setFileMsg(problems.join(' '));
  };
  const fileInputRef = useRef<HTMLInputElement>(null);

  const closeSvg = (
    <button className="modal-close" onClick={onClose} aria-label="Close">
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </button>
  );

  const segBtn = (active: boolean, first: boolean): CSSProperties => ({
    padding: '7px 16px',
    fontFamily: "'Sora', sans-serif",
    fontSize: '12.5px',
    fontWeight: 600,
    border: 'none',
    borderRight: first ? '1.5px solid var(--border)' : undefined,
    cursor: 'pointer',
    transition: 'background .12s,color .12s',
    background: active ? '#1a8a66' : 'var(--white)',
    color: active ? '#fff' : 'var(--text-muted)',
  });

  return (
    <>
      <div className="modal-backdrop" onClick={onClose}>
        <div
          className="modal-lg modal-wide"
          role="dialog"
          aria-modal="true"
          aria-label={`New ${contactType.name}`}
          ref={modalRef}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-head">
            <div>
              <h2>New {contactType.name}</h2>
              <p>Fill in as much as you have — you can edit anything later.</p>
            </div>
            {closeSvg}
          </div>
          <div className="modal-body">
            {
              <>
                <div className="field-grid">
                  {/* Contact status removed from the window (V3/V4) — the default
                      status is still applied silently on save. */}
                  <div className="field">
                    <label>Referral Source</label>
                    <ReferralCombo
                      value={f.referralSourceId ?? ''}
                      name={f.referralSourceName ?? ''}
                      onChange={(id, nm) =>
                        setF((p) => ({ ...p, referralSourceId: id, referralSourceName: nm }))
                      }
                    />
                    {isOtherReferral(f.referralSourceName) && (
                      <input
                        type="text"
                        placeholder={REFERRAL_SPECIFY_PLACEHOLDER}
                        data-field="referralSourceOther"
                        aria-label="Other referral source, please specify (required)"
                        aria-required
                        value={f.referralSourceOther ?? ''}
                        onChange={set('referralSourceOther')}
                        aria-invalid={referralOtherMissing || undefined}
                        style={{
                          marginTop: 8,
                          // Red right away while blank, so people know to fill it in.
                          ...(referralOtherMissing
                            ? {
                                borderColor: '#c0392b',
                                boxShadow: '0 0 0 3px rgba(192, 57, 43, 0.13)',
                              }
                            : {}),
                        }}
                      />
                    )}
                  </div>
                  <div className="field" style={{ marginTop: -2 }}>
                    <label style={{ marginBottom: 7 }}>Ownership</label>
                    <div
                      style={{
                        display: 'flex',
                        gap: 0,
                        border: '1.5px solid var(--border)',
                        borderRadius: 9,
                        overflow: 'hidden',
                        width: 'fit-content',
                      }}
                    >
                      <button
                        type="button"
                        style={segBtn(sharing === 'self', true)}
                        onClick={() => setSharing('self')}
                      >
                        Add for myself
                      </button>
                      <button
                        type="button"
                        style={segBtn(sharing === 'shared', false)}
                        onClick={() => {
                          setSharing('shared');
                          openShareFlow();
                        }}
                      >
                        Add for myself &amp; share
                      </button>
                    </div>
                  </div>
                  {sharing === 'shared' && shareCfg ? (
                    <div className="field field-full">
                      <div className="share-chip">
                        <span className="share-chip-text">
                          <svg
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#1a8a66"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{ flexShrink: 0 }}
                          >
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                          </svg>
                          <span>
                            <strong>Shared with:</strong>{' '}
                            {[
                              ...shareCfg.draft.users.map(
                                (id) => sharePeople?.find((x) => x.id === id)?.name ?? '',
                              ),
                              ...Object.keys(shareCfg.draft.roles).map(
                                (r) => ROLE_LABEL[r as Role] ?? r,
                              ),
                            ]
                              .filter(Boolean)
                              .join(', ')}{' '}
                            · {shareCfg.summary}
                          </span>
                        </span>
                        <span className="share-chip-actions">
                          <button type="button" className="share-chip-btn" onClick={openShareFlow}>
                            Edit
                          </button>
                          <button
                            type="button"
                            className="share-chip-btn"
                            onClick={() => {
                              setShareCfg(null);
                              setSharing('self');
                            }}
                          >
                            Remove
                          </button>
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="field" />
                  )}
                  <div
                    className="field field-full"
                    style={{ margin: '4px 0', borderTop: '1.5px solid var(--hairline, #e8eeed)' }}
                  />
                  {CLIENT_FIELDS.map((cf) => {
                    if (cf.type === 'subhead') {
                      return (
                        <div className="field field-full" key={cf.key}>
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              letterSpacing: '.09em',
                              textTransform: 'uppercase',
                              color: 'var(--text-muted-2)',
                              borderTop: '1px solid var(--border)',
                              paddingTop: 8,
                              marginTop: 2,
                            }}
                          >
                            {cf.label}
                          </div>
                        </div>
                      );
                    }
                    const badEmail = cf.type === 'email' && !isValidEmail(f[cf.key] ?? '');
                    const badPhone =
                      'mask' in cf && cf.mask === 'phone' && !phoneValid(f[cf.key] ?? '');
                    const badZip = 'mask' in cf && cf.mask === 'zip' && !zipValid(f[cf.key] ?? '');
                    // ehr#482: the phone or email another contact already has stays red
                    const taken = dupField(cf.key);
                    // ehr#757: a client's primary email left blank on a tried save
                    const emailBlank = cf.key === 'email' && blankRequired('email');
                    return (
                      <div
                        className={`field${cf.full ? ' field-full' : ''}`}
                        key={cf.key}
                        data-field={cf.key}
                      >
                        <label>{newClientLabel(cf.key, cf.label, category)}</label>
                        {cf.type === 'select' ? (
                          <>
                            <SearchableSelect
                              value={f[cf.key] ?? ''}
                              options={cf.options}
                              onChange={setV(cf.key)}
                            />
                            {cf.key === 'pronouns' && (f.pronouns ?? '') === 'Other' && (
                              <input
                                type="text"
                                placeholder="Please specify…"
                                value={f.pronounsOther ?? ''}
                                onChange={set('pronounsOther')}
                                style={{ marginTop: 8 }}
                              />
                            )}
                          </>
                        ) : (
                          <input
                            type={cf.type}
                            placeholder={cf.placeholder}
                            value={f[cf.key] ?? ''}
                            inputMode={cf.mask === 'zip' ? 'numeric' : undefined}
                            style={
                              badEmail || badPhone || badZip || taken || blankRequired(cf.key)
                                ? { borderColor: '#c0392b' }
                                : undefined
                            }
                            aria-invalid={taken || emailBlank || undefined}
                            onChange={
                              cf.mask === 'phone'
                                ? (e) => setV(cf.key)(maskPhone(e.target.value))
                                : cf.mask === 'zip'
                                  ? (e) => setV(cf.key)(maskZip(e.target.value))
                                  : set(cf.key)
                            }
                          />
                        )}
                        {badEmail && (
                          <span style={{ fontSize: 11, color: '#c0392b', marginTop: 4 }}>
                            Enter a valid email address
                          </span>
                        )}
                        {emailBlank && (
                          <span style={{ fontSize: 11, color: '#c0392b', marginTop: 4 }}>
                            Primary email is required
                          </span>
                        )}
                        {badPhone && (
                          <span style={{ fontSize: 11, color: '#c0392b', marginTop: 4 }}>
                            Enter a 10-digit phone
                          </span>
                        )}
                        {badZip && (
                          <span style={{ fontSize: 11, color: '#c0392b', marginTop: 4 }}>
                            Enter a 5-digit ZIP (or ZIP+4)
                          </span>
                        )}
                        {taken && dup && (
                          <span style={{ fontSize: 11, color: '#c0392b', marginTop: 4 }}>
                            {/* ehr#711: named only for someone who may open that chart */}
                            {openableDup(dup)
                              ? `Already used by ${openableDup(dup)!.name}`
                              : 'Already used by another chart'}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  <div
                    className="field field-full"
                    style={{ margin: '4px 0', borderTop: '1.5px solid var(--hairline, #e8eeed)' }}
                  />
                </div>
                <div
                  style={{ margin: '16px 0 0', borderTop: '1.5px solid var(--hairline, #e8eeed)' }}
                />
                <div style={{ marginTop: 16 }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--text-main)',
                      marginBottom: 8,
                      letterSpacing: '.01em',
                    }}
                  >
                    Attachments
                  </label>
                  <div
                    className="np-drop-zone"
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.add('drag');
                    }}
                    onDragLeave={(e) => e.currentTarget.classList.remove('drag')}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('drag');
                      addFiles(e.dataTransfer.files);
                    }}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#8aaa9a"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ marginBottom: 6 }}
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    <div style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 500 }}>
                      Drop files here or{' '}
                      <span style={{ color: '#1a8a66', fontWeight: 600 }}>browse</span>
                    </div>
                    <div style={{ fontSize: '11.5px', color: 'var(--text-muted-2)', marginTop: 3 }}>
                      {FILE_HINT} · attached after saving
                    </div>
                  </div>
                  {fileMsg && (
                    <div style={{ fontSize: '11.5px', color: 'var(--red)', marginTop: 6 }}>
                      {fileMsg}
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={FILE_ACCEPT}
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      addFiles(e.target.files);
                      e.target.value = '';
                    }}
                  />
                  {files.length > 0 && (
                    <div
                      style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}
                    >
                      {files.map((file, i) => (
                        <div
                          key={i}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 9,
                            padding: '7px 10px',
                            border: '1px solid var(--border)',
                            borderRadius: 8,
                            background: 'var(--white)',
                          }}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#527060"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                            <polyline points="13 2 13 9 20 9" />
                          </svg>
                          <span
                            style={{
                              fontSize: '12.5px',
                              color: 'var(--text-main)',
                              flex: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {file.name}
                          </span>
                          <span
                            style={{ fontSize: 11, color: 'var(--text-muted-2)', flexShrink: 0 }}
                          >
                            {fmtBytes(file.size)}
                          </span>
                          <button
                            type="button"
                            onClick={() => setFiles((p) => p.filter((_, idx) => idx !== i))}
                            style={{
                              border: 'none',
                              background: 'none',
                              cursor: 'pointer',
                              color: 'var(--text-muted-2)',
                              padding: 0,
                              lineHeight: 1,
                              fontSize: 15,
                              flexShrink: 0,
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {SHOW_ADMIN_ROUTING && isAdmin && (
                  <div className="np-admin-panel">
                    <button
                      type="button"
                      className={`np-admin-toggle${adminOpen ? ' open' : ''}`}
                      onClick={() => setAdminOpen((o) => !o)}
                    >
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 2L2 7l10 5 10-5-10-5z" />
                        <path d="M2 17l10 5 10-5" />
                        <path d="M2 12l10 5 10-5" />
                      </svg>
                      Testing &amp; Distribution
                      <span className="np-admin-badge">Admin</span>
                      <svg
                        className="np-chevron"
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ marginLeft: 'auto' }}
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                    <div className={`np-admin-body${adminOpen ? ' open' : ''}`}>
                      {['Distribution Rule', 'Redistribution Rule', 'Touch Rule'].map((l) => (
                        <div className="np-admin-field" key={l}>
                          <label>{l}</label>
                          <Dd
                            value=""
                            options={[]}
                            onChange={() => undefined}
                            placeholder="— None —"
                            searchable={false}
                          />
                        </div>
                      ))}
                      <p className="np-admin-hint">
                        Simulate routing for QA — these overrides are applied to this contact on
                        creation and are not saved to settings. Distribution / Redistribution rules
                        require a matching rule configured in Settings.
                      </p>
                    </div>
                  </div>
                )}
                {error && (
                  <p className="state error" style={{ marginTop: 14 }}>
                    {error}
                  </p>
                )}
              </>
            }
          </div>
          <div className="modal-foot">
            <button className="btn" onClick={onClose}>
              Cancel
            </button>
            <button className="btn primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
      {saveBlock && (
        <SaveBlockDialog
          block={saveBlock}
          sectionLabel={(sec) =>
            sec === NEW_CLIENT_REFERRAL_SEC
              ? 'Referral Source'
              : CLIENT_FIELDS.find((cf) => cf.key === sec)?.label
          }
          onGoTo={goToField}
          onClose={() => setSaveBlock(null)}
        />
      )}
      {dup && dupOpen && (
        <DuplicateContactDialog
          dup={dup}
          onOpen={onOpenExisting}
          onClose={() => {
            setDupOpen(false);
            // Go back: to the red field, to change it.
            requestAnimationFrame(() =>
              document.querySelector<HTMLInputElement>('input[aria-invalid="true"]')?.focus(),
            );
          }}
        />
      )}
      {shareModalOpen && sharePeople && (
        <ShareFlow
          mode="collect"
          targets={[{ id: '', name: 'The new contact', category, ownerUserId: user?.id ?? null }]}
          cap={creatorCap(user?.role)}
          people={sharePeople}
          meId={user?.id}
          start={shareCfg?.draft}
          onCancel={() => {
            setShareModalOpen(false);
            if (!shareCfg) setSharing('self');
          }}
          onCollect={(draft, summary) => {
            setShareCfg({ draft, summary });
            setSharing('shared');
            setShareModalOpen(false);
          }}
        />
      )}
    </>
  );
}

function fmtBytes(n: number): string {
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
  return (n / (1024 * 1024)).toFixed(1) + ' MB';
}

/** Kanban board — one column per pipeline stage (+ Unassigned); drag to move stage. */
function Board({
  items,
  stages,
  onOpen,
  onMove,
  onContext,
}: {
  items: Contact[];
  stages: Stage[];
  onOpen: (c: Contact) => void;
  onMove: (id: string, stageId: string | null) => void;
  onContext?: (
    e: { preventDefault: () => void; clientX: number; clientY: number },
    c: Contact,
  ) => void;
}) {
  const [over, setOver] = useState<string | null>(null);
  const columns = [
    ...stages.map((s) => ({ id: s.id as string | null, name: s.name })),
    { id: null, name: 'Unassigned' },
  ];
  return (
    <div className="board">
      {columns.map((col) => {
        const cards = items.filter((c) => (c.stageId ?? null) === col.id);
        const key = col.id ?? 'none';
        return (
          <div
            className={`board-col ${over === key ? 'drag-over' : ''}`}
            key={key}
            onDragOver={(e) => {
              e.preventDefault();
              setOver(key);
            }}
            onDragLeave={() => setOver((o) => (o === key ? null : o))}
            onDrop={(e) => {
              e.preventDefault();
              setOver(null);
              const id = e.dataTransfer.getData('text/contact-id');
              const dragged = items.find((c) => c.id === id);
              if (id && dragged && (dragged.stageId ?? null) !== col.id) onMove(id, col.id);
            }}
          >
            <div className="board-col-head">
              <span>{col.name}</span>
              <span className="board-col-count">{cards.length}</span>
            </div>
            <div className="board-body">
              {cards.length === 0 ? (
                <div className="board-empty">—</div>
              ) : (
                cards.map((c) => (
                  <button
                    className="board-card"
                    key={c.id}
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData('text/contact-id', c.id)}
                    onClick={() => onOpen(c)}
                    onContextMenu={onContext ? (e) => onContext(e, c) : undefined}
                  >
                    <Avatar c={c} />
                    <div className="name-cell-main">
                      {c.firstName} {c.lastName}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
